import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function StaffAdmin() {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState({ builtin: {}, custom: [], all_permissions: [] });
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "", role: "manager", permissions: [] });
  const [newRole, setNewRole] = useState({ name: "", permissions: [] });

  const load = useCallback(() => Promise.all([api.get("/admin/staff"), api.get("/admin/roles")]).then(([a,b]) => { setStaff(a.data); setRoles(b.data); }), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try { await api.post("/admin/staff", { ...newStaff, permissions: newStaff.permissions.length ? newStaff.permissions : null }); toast.success("Staff created"); load(); setNewStaff({ name: "", email: "", password: "", role: "manager", permissions: [] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const toggle = async (id, enabled) => { await api.put(`/admin/staff/${id}`, { enabled }); load(); };
  const del = async (id) => { if (confirm("Delete staff?")) { await api.delete(`/admin/staff/${id}`); load(); } };
  const createRole = async () => { await api.post("/admin/roles", newRole); toast.success("Role created"); setNewRole({ name: "", permissions: [] }); load(); };

  const togglePerm = (perms, p, set) => set(perms.includes(p) ? perms.filter(x => x !== p) : [...perms, p]);

  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Staff & Roles</h1>

      <div className="admin-surface p-6 mb-6">
        <p className="eyebrow mb-4">Create Staff Account</p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <input placeholder="Name" value={newStaff.name} onChange={(e) => setNewStaff({...newStaff, name: e.target.value})} className="admin-input" data-testid="admin-staff-name" />
          <input placeholder="Email" value={newStaff.email} onChange={(e) => setNewStaff({...newStaff, email: e.target.value})} className="admin-input" data-testid="admin-staff-email" />
          <input placeholder="Password" type="password" value={newStaff.password} onChange={(e) => setNewStaff({...newStaff, password: e.target.value})} className="admin-input" data-testid="admin-staff-password" />
          <select value={newStaff.role} onChange={(e) => setNewStaff({...newStaff, role: e.target.value, permissions: []})} className="admin-input" data-testid="admin-staff-role">
            {Object.keys(roles.builtin).filter(r => r !== "super_admin" && r !== "customer").map(r => <option key={r} value={r}>{r}</option>)}
            {roles.custom.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
          </select>
          <div className="col-span-2">
            <p className="text-xs text-[#8C867D] mb-2">Custom permissions (optional — overrides role defaults):</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {roles.all_permissions.map(p => (
                <label key={p} className="flex items-center gap-1"><input type="checkbox" checked={newStaff.permissions.includes(p)} onChange={() => togglePerm(newStaff.permissions, p, (v) => setNewStaff({...newStaff, permissions: v}))} data-testid={`admin-staff-perm-${p}`} /> {p}</label>
              ))}
            </div>
          </div>
          <button onClick={create} className="col-span-2 bg-[#C5A059] text-[#121212] py-2 text-xs uppercase tracking-widest" data-testid="admin-staff-create">Create</button>
        </div>
      </div>

      <div className="admin-surface p-6 mb-6">
        <p className="eyebrow mb-4">Staff Accounts</p>
        <table className="w-full text-sm">
          <thead className="text-[#8C867D] text-xs uppercase tracking-widest text-left">
            <tr><th className="p-2">Name</th><th>Email</th><th>Role</th><th>Enabled</th><th></th></tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.id} className="border-t border-[#27272A]">
                <td className="p-2">{s.name}</td>
                <td className="text-xs">{s.email}</td>
                <td className="text-xs uppercase">{s.role}</td>
                <td><input type="checkbox" checked={!!s.enabled} onChange={(e) => toggle(s.id, e.target.checked)} disabled={s.role === "super_admin"} data-testid={`admin-staff-toggle-${s.email}`} /></td>
                <td><button onClick={() => del(s.id)} disabled={s.role === "super_admin"} className="text-xs uppercase text-[#5B1C22] disabled:opacity-30">Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-surface p-6">
        <p className="eyebrow mb-4">Create Custom Role</p>
        <div className="flex gap-3 items-start">
          <input placeholder="Role Name" value={newRole.name} onChange={(e) => setNewRole({...newRole, name: e.target.value})} className="admin-input" data-testid="admin-role-name" />
          <div className="grid grid-cols-4 gap-2 text-xs flex-1">
            {roles.all_permissions.map(p => (
              <label key={p} className="flex items-center gap-1"><input type="checkbox" checked={newRole.permissions.includes(p)} onChange={() => togglePerm(newRole.permissions, p, (v) => setNewRole({...newRole, permissions: v}))} /> {p}</label>
            ))}
          </div>
          <button onClick={createRole} className="bg-[#C5A059] text-[#121212] px-4 py-2 text-xs uppercase tracking-widest" data-testid="admin-role-create">Save Role</button>
        </div>
      </div>
    </div>
  );
}
